import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PAYMENT_STATUS, INVOICE_STATUS, eur, money, fmtDate, fmtDateTime, CURRENCY_SYMBOLS } from "../lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthFileLink from "./AuthFileLink";
import { useConfirm } from "./ConfirmDialog";

const today = () => new Date().toISOString().slice(0, 10);
const dOnly = (v) => (v ? String(v).slice(0, 10) : "");

const SummaryCard = ({ label, value, accent, testid }) => (
  <div className="bg-sable rounded-sm border border-marine/10 p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-marine/80 mb-1">{label}</div>
    <div className={`font-display text-2xl ${accent || "text-marine"}`}>{value}</div>
  </div>
);

const InstallmentCard = ({ pid, inst, onChanged, locked, cur = "EUR" }) => {
  const { confirm } = useConfirm();
  const m = (n) => money(n, cur);
  const [f, setF] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [np, setNp] = useState({ amount: "", date: today(), note: "" });
  useEffect(() => {
    setF({
      label: inst.label || "",
      amount: inst.amount ?? "",
      paid_amount: inst.paid_amount ?? "",
      due_date: dOnly(inst.due_date),
      status: inst.status || "a_venir",
      freeze_status: !!inst.freeze_status,
      paid_date: dOnly(inst.paid_date),
      invoice_number: inst.invoice_number || "",
      invoice_link: inst.invoice_link || "",
      invoice_status: inst.invoice_status || "emise",
      note: inst.note || "",
      notify_mode: inst.notify_mode || "auto",
    });
  }, [inst]);
  if (!f) return null;

  const eff = inst.effective_status || inst.status;
  const meta = PAYMENT_STATUS[eff] || PAYMENT_STATUS.a_venir;
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, amount: parseFloat(f.amount) || 0, paid_amount: f.paid_amount === "" ? null : parseFloat(f.paid_amount) || 0 };
      await api.put(`/admin/projects/${pid}/installments/${inst.id}`, payload);
      toast.success("Échéance enregistrée"); onChanged();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); }
  };
  const markPaid = async () => {
    const remaining = Math.max((parseFloat(inst.amount) || 0) - paidSum, 0);
    if (remaining <= 0) { toast.info("Déjà soldée"); return; }
    try { await api.post(`/admin/projects/${pid}/installments/${inst.id}/payments`, { amount: remaining, date: today(), note: "Solde réglé" }); toast.success("Marquée payée"); onChanged(); }
    catch { toast.error("Erreur"); }
  };
  const del = async () => { if (!(await confirm({ title: `Supprimer l'échéance « ${inst.label} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; await api.delete(`/admin/projects/${pid}/installments/${inst.id}`); toast.success("Échéance supprimée"); onChanged(); };
  const notify = async () => {
    try { const r = await api.post(`/admin/projects/${pid}/installments/${inst.id}/notify`); toast.success(r.data.ok ? "Entreprise notifiée" : "Notification non envoyée (déjà envoyée ou masquée)"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const uploadInvoice = async (e) => { const file = e.target.files[0]; if (!file) return; const fd = new FormData(); fd.append("file", file); try { await api.post(`/admin/projects/${pid}/installments/${inst.id}/invoice`, fd); toast.success("Facture ajoutée"); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); } finally { e.target.value = ""; } };
  const removeInvoice = async () => { await api.delete(`/admin/projects/${pid}/installments/${inst.id}/invoice`); toast.success("Facture supprimée"); onChanged(); };
  const payments = inst.payments || [];
  const paidSum = payments.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const addPayment = async () => {
    const amt = parseFloat(np.amount) || 0;
    if (amt <= 0) return toast.error("Montant du paiement invalide");
    try { await api.post(`/admin/projects/${pid}/installments/${inst.id}/payments`, { amount: amt, date: np.date, note: np.note }); toast.success("Paiement enregistré"); setNp({ amount: "", date: today(), note: "" }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const delPayment = async (payid) => { try { await api.delete(`/admin/projects/${pid}/installments/${inst.id}/payments/${payid}`); toast.success("Paiement supprimé"); onChanged(); } catch { toast.error("Erreur"); } };

  const onStatusChange = (v) => {
    const next = { ...f, status: v };
    if (v === "paye") { if (!next.paid_date) next.paid_date = today(); if (next.paid_amount === "" || next.paid_amount == null) next.paid_amount = f.amount; }
    if (v === "partiellement_paye" && !next.paid_date) next.paid_date = today();
    setF(next);
  };

  return (
    <div className="border border-marine/15 rounded-sm" data-testid={`installment-${inst.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`installment-toggle-${inst.id}`}
        className="w-full text-left flex items-center justify-between gap-3 p-4 hover:bg-creme transition-colors rounded-sm">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <Icons.ChevronRight size={16} className={`shrink-0 text-bordeaux transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="font-display text-lg text-marine truncate">{inst.label}</span>
          <span className={`shrink-0 text-[9px] px-2 py-0.5 rounded-sm border ${meta.cls}`} data-testid={`installment-status-${inst.id}`}>{meta.label}</span>
          {(() => { const is = INVOICE_STATUS[inst.invoice_status || "emise"]; return <span className={`shrink-0 text-[9px] px-2 py-0.5 rounded-sm border ${is.cls}`} data-testid={`installment-invoice-status-${inst.id}`}>Facture : {is.label}</span>; })()}
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="hidden sm:flex flex-col items-end text-xs text-marine/80 leading-tight">
            <span className="inline-flex items-center gap-1"><Icons.Calendar size={16} className="text-marine" /> {inst.due_date ? fmtDate(inst.due_date) : "—"}</span>
            <span>Encaissé {m(paidSum)} · Reste {m(Math.max((parseFloat(inst.amount) || 0) - paidSum, 0))}</span>
          </div>
          <div className="font-display text-lg text-marine">{m(inst.amount)}</div>
        </div>
      </button>

      {open && (
      <div className="px-4 pb-4">
      <fieldset disabled={locked} className={locked ? "opacity-60" : ""}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="text-sm">Libellé<input value={f.label} onChange={(e) => set("label", e.target.value)} data-testid={`inst-label-${inst.id}`} className="inp mt-1" placeholder="Acompte, Solde..." /></label>
        <label className="text-sm">Montant prévu (€)<input type="number" min="0" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid={`inst-amount-${inst.id}`} className="inp mt-1" /></label>
        <label className="text-sm">Date d'échéance<input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid={`inst-due-${inst.id}`} className="inp mt-1" /></label>
        <label className="text-sm">Statut du paiement
          <select value={f.status} onChange={(e) => onStatusChange(e.target.value)} data-testid={`inst-status-${inst.id}`} className="inp mt-1">
            {Object.entries(PAYMENT_STATUS)
              .filter(([k]) => k !== "partiellement_paye" || f.status === "partiellement_paye")
              .map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
          <span className="block text-[10px] text-marine/80 mt-1">Les montants encaissés se saisissent dans « Paiements rattachés » ci-dessous.</span>
        </label>
        <label className="text-sm">N° de facture<input value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} data-testid={`inst-invoice-${inst.id}`} className="inp mt-1" placeholder="FAC-2026-001" /></label>
        <label className="text-sm">Statut de la facture
          <select value={f.invoice_status} onChange={(e) => set("invoice_status", e.target.value)} data-testid={`inst-invoice-status-${inst.id}`} className="inp mt-1">
            {Object.entries(INVOICE_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-sm">Lien de facture (externe)<input value={f.invoice_link} onChange={(e) => set("invoice_link", e.target.value)} className="inp mt-1" placeholder="https://..." /></label>
        <label className="text-sm">Notification entreprise
          <select value={f.notify_mode} onChange={(e) => set("notify_mode", e.target.value)} data-testid={`inst-notify-${inst.id}`} className="inp mt-1">
            <option value="auto">Automatique</option>
            <option value="manuel">Manuelle</option>
            <option value="off">Désactivée</option>
          </select>
        </label>
      </div>

      <label className="text-sm block mt-3">Note interne <span className="text-[10px] uppercase tracking-wider text-bordeaux inline-flex items-center gap-1"><Icons.Lock size={16} /> jamais visible par l'entreprise</span>
        <textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} className="inp mt-1" data-testid={`inst-note-${inst.id}`} />
      </label>

      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`inst-freeze-${inst.id}`}>
          <input type="checkbox" checked={f.freeze_status} onChange={(e) => set("freeze_status", e.target.checked)} /> Figer le statut (ignorer le retard auto)
        </label>
        <span className="text-[11px] text-marine/80 inline-flex items-center gap-1"><Icons.Info size={16} className="text-marine" /> L'échéance est visible par l'entreprise. La facture apparaît automatiquement à la date d'échéance.</span>
      </div>

      <div className="mt-3 pt-3 border-t border-marine/10" data-testid={`inst-payments-${inst.id}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold flex items-center gap-1.5"><Icons.Coins size={16} /> Paiements rattachés{payments.length > 0 ? ` (${payments.length})` : ""}</div>
          <span className="text-xs text-marine/80">Encaissé {m(paidSum)} / {m(inst.amount)} · Reste {m(Math.max((parseFloat(inst.amount) || 0) - paidSum, 0))}</span>
        </div>
        <div className="space-y-1.5 mb-2">
          {payments.length === 0 && <p className="text-xs text-marine/80">Aucun paiement enregistré. Ajoutez chaque encaissement ci-dessous (règlement en une ou plusieurs fois).</p>}
          {payments.map((pay) => (
            <div key={pay.id} data-testid={`payment-${pay.id}`} className="flex items-center gap-3 text-sm bg-creme border border-marine/10 rounded-sm px-3 py-1.5">
              <Icons.CheckCircle2 size={16} className="text-bleu shrink-0" />
              <span className="font-medium text-marine">{m(pay.amount)}</span>
              <span className="text-marine/80">{fmtDate(pay.date)}</span>
              {pay.note && <span className="text-marine/80 truncate italic">— {pay.note}</span>}
              <button onClick={() => delPayment(pay.id)} data-testid={`payment-del-${pay.id}`} className="ml-auto text-rouge hover:opacity-70 shrink-0"><Icons.Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-[120px_150px_1fr_auto] gap-2">
          <input type="number" min="0" placeholder="Montant €" value={np.amount} onChange={(e) => setNp({ ...np, amount: e.target.value })} data-testid={`payment-amount-${inst.id}`} className="inp" />
          <input type="date" value={np.date} onChange={(e) => setNp({ ...np, date: e.target.value })} data-testid={`payment-date-${inst.id}`} className="inp" />
          <input placeholder="Note interne (optionnel)" title="Jamais visible par l'entreprise" aria-label="Note interne du paiement, jamais visible par l'entreprise" value={np.note} onChange={(e) => setNp({ ...np, note: e.target.value })} data-testid={`payment-note-${inst.id}`} className="inp" />
          <button onClick={addPayment} data-testid={`payment-add-${inst.id}`} className="bg-marine text-white rounded-sm text-sm px-4 py-2 hover:bg-marine-hover transition-colors inline-flex items-center gap-1"><Icons.Plus size={16} /> Ajouter</button>
        </div>
        <p className="text-[11px] text-marine/80 mt-1.5">Saisissez ici chaque encaissement (en une ou plusieurs fois). Le statut du paiement et le montant encaissé sont calculés automatiquement à partir des paiements rattachés.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-marine/10">
        <div className="flex items-center gap-2 mr-auto text-xs text-marine/80">
          {inst.invoice_url ? (
            <>
              <AuthFileLink path={inst.invoice_url} className="inline-flex items-center gap-1 text-marine hover:underline"><Icons.FileText size={16} /> {inst.invoice_name || "Facture"}</AuthFileLink>
              <button onClick={removeInvoice} className="text-rouge hover:underline">Retirer</button>
            </>
          ) : (
            <label className="inline-flex items-center gap-1 text-marine cursor-pointer hover:underline"><Icons.Upload size={16} /> Joindre une facture<input type="file" className="hidden" onChange={uploadInvoice} data-testid={`inst-invoice-upload-${inst.id}`} /></label>
          )}
        </div>
        {inst.last_notified_at && <span className="text-xs text-marine/80">Notifié {inst.last_notified_type ? `(${PAYMENT_STATUS[inst.last_notified_type]?.label || inst.last_notified_type})` : ""} le {fmtDateTime(inst.last_notified_at)}</span>}
        <button onClick={notify} data-testid={`inst-notify-btn-${inst.id}`} className="text-sm border border-marine/30 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors inline-flex items-center gap-1"><Icons.Bell size={16} /> Notifier</button>
        {eff !== "paye" && <button onClick={markPaid} data-testid={`inst-markpaid-${inst.id}`} className="text-sm border border-marine text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors inline-flex items-center gap-1"><Icons.Check size={16} /> Payé</button>}
        <button onClick={del} data-testid={`inst-delete-${inst.id}`} className="text-sm border border-rouge text-rouge rounded-sm px-3 py-1.5 hover:bg-rouge hover:text-white transition-colors inline-flex items-center gap-1"><Icons.Trash2 size={16} /> Supprimer</button>
        <button onClick={save} disabled={saving} data-testid={`inst-save-${inst.id}`} className="text-sm bg-bordeaux text-white rounded-sm px-4 py-1.5 hover:bg-bordeaux-hover transition-colors">{saving ? "..." : "Enregistrer"}</button>
      </div>
      </fieldset>
      </div>
      )}
    </div>
  );
};

export default function ProjectBilling({ p, reload }) {
  const { confirm, choose } = useConfirm();
  const pid = p.id;
  const cur = p.currency || "EUR";
  const m = (n) => money(n, cur);
  const fin = p.financials || {};
  const locked = !!p.billing_validated;
  const [amount, setAmount] = useState(fin.total ?? p.amount ?? "");
  const [mode, setMode] = useState(p.billing_mode || "unique");
  useEffect(() => { setAmount((p.financials || {}).total ?? p.amount ?? ""); setMode(p.billing_mode || "unique"); }, [p]);

  const saveHeader = async () => {
    try { await api.put(`/admin/projects/${pid}`, { amount: parseFloat(amount) || 0, billing_mode: mode }); toast.success("Facturation enregistrée"); reload(); }
    catch { toast.error("Erreur"); }
  };
  const [genCount, setGenCount] = useState(3);
  const generate = async () => {
    const n = parseInt(genCount) || 0;
    if (n < 1) { toast.error("Indiquez un nombre d'échéances"); return; }
    const has = (p.installments || []).length > 0;
    let replace = true;
    if (has) {
      // Deux vrais choix (la boîte native faisait « Annuler » = ajouter) ; fermer la fenêtre = ne rien créer.
      const choice = await choose({ title: `Créer ${n} échéance(s) vierges ?`, message: "Un échéancier existe déjà : ajouter les nouvelles échéances à la suite, ou le remplacer entièrement ?",
        actions: [{ label: "Ajouter à la suite", value: "add", tone: "quiet" }, { label: "Remplacer l'échéancier", value: "replace", tone: "danger" }] });
      if (!choice) return;
      replace = choice === "replace";
    }
    try {
      await api.post(`/admin/projects/${pid}/installments/generate`, { count: n, replace, distribute: false, frequency: "none" });
      toast.success(`${n} échéance(s) créées — renseignez le montant et la date de chacune`); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const validateBilling = async () => {
    try { await api.post(`/admin/projects/${pid}/billing/validate`); toast.success("Facturation validée et verrouillée"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Validation impossible"); }
  };
  const unlockBilling = async () => {
    if (!(await confirm({ title: "Déverrouiller la facturation ?", message: "Elle repassera en brouillon et devra être revalidée après modification.", confirmLabel: "Déverrouiller" }))) return;
    try { await api.post(`/admin/projects/${pid}/billing/unlock`); toast.success("Facturation déverrouillée (brouillon)"); reload(); }
    catch { toast.error("Erreur"); }
  };
  const addInstallment = async () => {
    const n = (p.installments || []).length + 1;
    await api.post(`/admin/projects/${pid}/installments`, { label: `Échéance ${n}`, amount: 0, status: "a_venir", notify_mode: "auto" });
    toast.success("Échéance ajoutée"); reload();
  };

  return (
    <div className="bg-sable rounded-sm border border-bordeaux/25 p-5 mb-8" data-testid="payment-section">
      <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Facturation & paiement</h2><span className="text-[9px] uppercase tracking-wider bg-bordeaux-50 text-bordeaux border border-bordeaux/30 px-2 py-0.5 rounded-sm flex items-center gap-1">Admin uniquement</span></div>
      <p className="text-xs text-marine/80 mb-4">Ces informations financières ne sont jamais visibles par les collaborateurs. L'entreprise voit toutes ses échéances et en est prévenue selon le mode choisi pour chacune.</p>

      {locked && (
        <div className="flex items-center justify-between gap-3 mb-4 bg-bleu-50 border border-bleu/40 rounded-sm px-3 py-2.5" data-testid="billing-lock-banner">
          <span className="flex items-center gap-2 text-sm text-bleu"><Icons.Lock size={16} /> Facturation validée et <b>verrouillée</b> — modification impossible sans déverrouillage.</span>
          <button onClick={unlockBilling} data-testid="unlock-billing" className="shrink-0 border border-bordeaux text-bordeaux rounded-sm px-3 py-1.5 text-sm hover:bg-bordeaux hover:text-white transition-colors inline-flex items-center gap-1.5"><Icons.LockOpen size={16} /> Déverrouiller</button>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4 mb-5">
        <label className="text-sm">Mode de facturation
          <select value={mode} onChange={(e) => setMode(e.target.value)} disabled={locked} data-testid="billing-mode" className="inp mt-1 disabled:opacity-50">
            <option value="unique">Paiement unique</option>
            <option value="echeances">Paiement en plusieurs échéances</option>
          </select>
        </label>
        <label className="text-sm">Montant total du projet ({CURRENCY_SYMBOLS[cur] || cur})<input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={locked} data-testid="project-total-amount" className="inp mt-1 disabled:opacity-50" placeholder="Devis accepté" /></label>
        <button onClick={saveHeader} disabled={locked} data-testid="billing-header-save" className="border border-bordeaux text-bordeaux rounded-sm px-4 py-2.5 text-sm hover:bg-bordeaux hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-bordeaux">Enregistrer</button>
      </div>

      {mode === "echeances" && !locked && (
        <div className="flex flex-wrap items-end gap-3 mb-5 bg-creme border border-marine/10 rounded-sm p-3" data-testid="installment-generator">
          <div className="text-xs uppercase tracking-wider text-marine w-full mb-1">Créer un échéancier</div>
          <label className="text-sm">Nombre d'échéances<input type="number" min="1" max="60" value={genCount} onChange={(e) => setGenCount(e.target.value)} data-testid="gen-count" className="inp mt-1 w-32" /></label>
          <button onClick={generate} data-testid="gen-run" className="bg-marine text-white rounded-sm px-4 py-2.5 text-sm hover:bg-marine-hover transition-colors inline-flex items-center gap-1"><Icons.ListPlus size={16} /> Créer les échéances</button>
          <span className="text-[11px] text-marine/80 pb-2.5">Crée des échéances vierges — vous définissez librement le montant et la date de chacune.</span>
        </div>
      )}

      <h3 className="text-sm font-medium text-marine mb-2 uppercase tracking-wider">Résumé financier</h3>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <SummaryCard testid="fin-total" label="Montant total" value={m(fin.total)} />
        <SummaryCard testid="fin-scheduled" label="Échéances programmées" value={m(fin.scheduled)} />
        <SummaryCard testid="fin-encaisse" label="Encaissé" value={m(fin.encaisse)} accent="text-bleu" />
        <SummaryCard testid="fin-reste" label="Reste à encaisser" value={m(fin.reste_a_encaisser)} accent="text-bordeaux" />
        <SummaryCard testid="fin-planifier" label="Reste à planifier" value={m(fin.reste_a_planifier)} />
      </div>
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1 h-2 bg-sable border border-marine/30 rounded-full overflow-hidden"><div className="h-full bg-bleu" style={{ width: `${Math.min(fin.percent_paid || 0, 100)}%` }} /></div>
        <span className="text-sm text-marine/80">{fin.percent_paid || 0}% réglé</span>
      </div>
      {fin.balanced ? (
        <div className="flex items-center justify-between gap-3 mb-4" data-testid="billing-balanced">
          <div className="flex items-center gap-2 text-sm text-bleu bg-bleu-50 border border-bleu/40 rounded-sm px-3 py-2"><Icons.CheckCircle2 size={16} /> Échéancier équilibré — le total des échéances correspond au montant du projet.</div>
          {p.billing_validated
            ? <span className="inline-flex items-center gap-1.5 text-sm text-bleu font-medium" data-testid="billing-validated-badge"><Icons.BadgeCheck size={16} /> Facturation validée</span>
            : <button onClick={validateBilling} data-testid="validate-billing" className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover transition-colors inline-flex items-center gap-1.5"><Icons.Check size={16} /> Valider la facturation</button>}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-rouge bg-rouge-50 border border-rouge/40 rounded-sm px-3 py-2 mb-4" data-testid="billing-gap">
          <Icons.AlertTriangle size={16} />
          <span>Écart de <b>{fin.gap > 0 ? "+" : ""}{m(fin.gap)}</b> — {fin.gap > 0 ? `il reste ${m(fin.gap)} à répartir` : `les échéances dépassent le total de ${m(Math.abs(fin.gap))}`}. La validation est bloquée tant que la somme des échéances ≠ montant du projet ({m(fin.total)}).</span>
        </div>
      )}

      <div className="flex items-center justify-between mt-5 mb-3">
        <h3 className="text-sm font-medium text-marine uppercase tracking-wider">Échéancier de paiement</h3>
        {!locked && <button onClick={addInstallment} data-testid="add-installment" className="text-sm bg-marine text-white rounded-sm px-4 py-2 hover:bg-marine-hover transition-colors inline-flex items-center gap-1"><Icons.Plus size={16} /> Ajouter une échéance</button>}
      </div>
      <div className="space-y-3">
        {(p.installments || []).length === 0 && <p className="text-sm text-marine/80">Aucune échéance. Ajoutez-en une pour construire l'échéancier.</p>}
        {(p.installments || []).map((inst) => <InstallmentCard key={inst.id} pid={pid} inst={inst} onChanged={reload} locked={locked} cur={cur} />)}
      </div>
    </div>
  );
}
